export const PLAN_KEYS = {
  STARTER: "starter",
  PRO: "pro",
  ENTERPRISE: "enterprise",
};

export const FEATURE_KEYS = {
  BASIC_REPORTS: "basicReports",
  INVESTOR_REPORTS: "investorReports",
  AI_RECOMMENDATIONS: "aiRecommendations",
  AI_DRAFT: "aiDraft",
  COPILOT: "copilot",
  MATERIALITY_ANALYSIS: "materialityAnalysis",
  BENCHMARK_COMPARISON: "benchmarkComparison",
  PDF_EXPORT: "pdfExport",
  ADVANCED_ANALYTICS: "advancedAnalytics",
  UNLIMITED_PDF_EXPORTS: "unlimitedPdfExports",
  BOARD_READY_REPORTING: "boardReadyReporting",
  MULTI_REPORT_WORKFLOWS: "multiReportWorkflows",
  TEAM_COLLABORATION: "teamCollaboration",
  APPROVAL_WORKFLOW: "approvalWorkflow",
  CUSTOM_BRANDING: "customBranding",
  API_ACCESS: "apiAccess",
  SSO: "sso",
};

export const PLANS = {
  [PLAN_KEYS.STARTER]: {
    key: PLAN_KEYS.STARTER,
    name: "Starter",
    tagline: "Start ESG readiness with investor-grade AI reporting.",
    cta: "Start Starter Plan",
    monthlyPrice: "€49",
    annualPrice: "€470",
    annualSavings: "Save 20%",
    monthlyCredits: 10,
    maxReports: 10,
    highlighted: false,
    bestFor: "SMEs and growing organizations beginning ESG reporting.",
    features: [
      "Investor-grade ESG reports",
      "Materiality analysis",
      "Benchmark comparison",
      "PDF export",
      "CSRD readiness insights",
      "Audit trail",
      "Priority email support",
    ],
  },

  [PLAN_KEYS.PRO]: {
    key: PLAN_KEYS.PRO,
    name: "Pro",
    tagline: "Scale ESG intelligence with advanced governance analytics.",
    cta: "Choose Pro",
    monthlyPrice: "€149",
    annualPrice: "€1,430",
    annualSavings: "Save 20%",
    monthlyCredits: 50,
    maxReports: 50,
    highlighted: true,
    bestFor:
      "Organizations with active ESG governance and recurring reporting.",
    features: [
      "Everything in Starter",
      "Ecovanta Copilot",
      "AI recommendations",
      "Advanced ESG analytics",
      "Unlimited PDF exports",
      "Board-ready reporting",
      "Advanced benchmarking",
      "Multi-report workflows",
      "AI governance insights",
      "Enhanced data intelligence",
      "Audit trail",
      "Priority support",
    ],
  },

  [PLAN_KEYS.ENTERPRISE]: {
    key: PLAN_KEYS.ENTERPRISE,
    name: "Enterprise",
    tagline: "Enterprise-grade ESG intelligence infrastructure.",
    cta: "Choose Enterprise",
    monthlyPrice: "€599",
    annualPrice: "€5,750",
    annualSavings: "Save 20%",
    monthlyCredits: 250,
    maxReports: 250,
    highlighted: false,
    bestFor:
      "Large enterprises, institutions, consultants, and ESG program offices.",
    features: [
      "Everything in Pro",
      "Team collaboration workflows",
      "Approval & review governance",
      "Custom branding",
      "API access",
      "Dedicated onboarding",
      "Enterprise integrations",
      "SSO & enterprise security",
      "Dedicated customer success",
      "Scalable ESG intelligence operations",
    ],
  },
};

const ACCESS_MATRIX = {
  [PLAN_KEYS.STARTER]: {
    [FEATURE_KEYS.BASIC_REPORTS]: true,
    [FEATURE_KEYS.INVESTOR_REPORTS]: true,
    [FEATURE_KEYS.AI_RECOMMENDATIONS]: true,
    [FEATURE_KEYS.AI_DRAFT]: false,
    [FEATURE_KEYS.COPILOT]: false,
    [FEATURE_KEYS.MATERIALITY_ANALYSIS]: true,
    [FEATURE_KEYS.BENCHMARK_COMPARISON]: true,
    [FEATURE_KEYS.PDF_EXPORT]: true,
    [FEATURE_KEYS.ADVANCED_ANALYTICS]: false,
    [FEATURE_KEYS.UNLIMITED_PDF_EXPORTS]: false,
    [FEATURE_KEYS.BOARD_READY_REPORTING]: false,
    [FEATURE_KEYS.MULTI_REPORT_WORKFLOWS]: false,
    [FEATURE_KEYS.TEAM_COLLABORATION]: false,
    [FEATURE_KEYS.APPROVAL_WORKFLOW]: false,
    [FEATURE_KEYS.CUSTOM_BRANDING]: false,
    [FEATURE_KEYS.API_ACCESS]: false,
    [FEATURE_KEYS.SSO]: false,
  },

  [PLAN_KEYS.PRO]: {
    [FEATURE_KEYS.BASIC_REPORTS]: true,
    [FEATURE_KEYS.INVESTOR_REPORTS]: true,
    [FEATURE_KEYS.AI_RECOMMENDATIONS]: true,
    [FEATURE_KEYS.AI_DRAFT]: true,
    [FEATURE_KEYS.COPILOT]: true,
    [FEATURE_KEYS.MATERIALITY_ANALYSIS]: true,
    [FEATURE_KEYS.BENCHMARK_COMPARISON]: true,
    [FEATURE_KEYS.PDF_EXPORT]: true,
    [FEATURE_KEYS.ADVANCED_ANALYTICS]: true,
    [FEATURE_KEYS.UNLIMITED_PDF_EXPORTS]: true,
    [FEATURE_KEYS.BOARD_READY_REPORTING]: true,
    [FEATURE_KEYS.MULTI_REPORT_WORKFLOWS]: true,
    [FEATURE_KEYS.TEAM_COLLABORATION]: false,
    [FEATURE_KEYS.APPROVAL_WORKFLOW]: false,
    [FEATURE_KEYS.CUSTOM_BRANDING]: false,
    [FEATURE_KEYS.API_ACCESS]: false,
    [FEATURE_KEYS.SSO]: false,
  },

  [PLAN_KEYS.ENTERPRISE]: {
    [FEATURE_KEYS.BASIC_REPORTS]: true,
    [FEATURE_KEYS.INVESTOR_REPORTS]: true,
    [FEATURE_KEYS.AI_RECOMMENDATIONS]: true,
    [FEATURE_KEYS.AI_DRAFT]: true,
    [FEATURE_KEYS.COPILOT]: true,
    [FEATURE_KEYS.MATERIALITY_ANALYSIS]: true,
    [FEATURE_KEYS.BENCHMARK_COMPARISON]: true,
    [FEATURE_KEYS.PDF_EXPORT]: true,
    [FEATURE_KEYS.ADVANCED_ANALYTICS]: true,
    [FEATURE_KEYS.UNLIMITED_PDF_EXPORTS]: true,
    [FEATURE_KEYS.BOARD_READY_REPORTING]: true,
    [FEATURE_KEYS.MULTI_REPORT_WORKFLOWS]: true,
    [FEATURE_KEYS.TEAM_COLLABORATION]: true,
    [FEATURE_KEYS.APPROVAL_WORKFLOW]: true,
    [FEATURE_KEYS.CUSTOM_BRANDING]: true,
    [FEATURE_KEYS.API_ACCESS]: true,
    [FEATURE_KEYS.SSO]: true,
  },
};

const FEATURE_UPGRADE_PLANS = {
  [FEATURE_KEYS.AI_DRAFT]: PLAN_KEYS.PRO,
  [FEATURE_KEYS.COPILOT]: PLAN_KEYS.PRO,
  [FEATURE_KEYS.ADVANCED_ANALYTICS]: PLAN_KEYS.PRO,
  [FEATURE_KEYS.UNLIMITED_PDF_EXPORTS]: PLAN_KEYS.PRO,
  [FEATURE_KEYS.BOARD_READY_REPORTING]: PLAN_KEYS.PRO,
  [FEATURE_KEYS.MULTI_REPORT_WORKFLOWS]: PLAN_KEYS.PRO,

  [FEATURE_KEYS.TEAM_COLLABORATION]: PLAN_KEYS.ENTERPRISE,
  [FEATURE_KEYS.APPROVAL_WORKFLOW]: PLAN_KEYS.ENTERPRISE,
  [FEATURE_KEYS.CUSTOM_BRANDING]: PLAN_KEYS.ENTERPRISE,
  [FEATURE_KEYS.API_ACCESS]: PLAN_KEYS.ENTERPRISE,
  [FEATURE_KEYS.SSO]: PLAN_KEYS.ENTERPRISE,
};

const CREDIT_USAGE = {
  basicReport: 1,
  investorGradeReport: 1,
  aiRecommendations: 1,
  materialityAnalysis: 2,
  benchmarkComparison: 1,
  advancedAnalytics: 2,
  multiLanguageReport: 2,
  boardReadyExport: 2,
};

export function normalizePlanKey(planKey = "") {
  const normalized = String(planKey).trim().toLowerCase();

  return PLANS[normalized] ? normalized : PLAN_KEYS.STARTER;
}

export function getPlan(planKey) {
  return PLANS[normalizePlanKey(planKey)];
}

export function getPlanConfig(planKey) {
  return getPlan(planKey);
}

export function getPlanLimits(planKey) {
  const plan = getPlan(planKey);

  return {
    monthlyCredits: plan.monthlyCredits,
    maxReports: plan.maxReports,
  };
}

export function getPlanPricing(planKey, billingCycle = "monthly") {
  const plan = getPlan(planKey);
  const normalizedBillingCycle = String(billingCycle).trim().toLowerCase();

  return normalizedBillingCycle === "annual"
    ? plan.annualPrice
    : plan.monthlyPrice;
}

export function canAccess(planKey, feature) {
  if (!feature) {
    return false;
  }

  const normalizedPlan = normalizePlanKey(planKey);
  const permissions = ACCESS_MATRIX[normalizedPlan];

  return Boolean(permissions?.[feature]);
}

export function getUpgradeRecommendation(feature) {
  return FEATURE_UPGRADE_PLANS[feature] || PLAN_KEYS.PRO;
}

export function getCreditUsage(operation) {
  return CREDIT_USAGE[operation] || 1;
}

// Backward-compatible alias retained for existing imports.
export const PLAN_CONFIG = PLANS;
